import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from '@/lib/i18n';
import {
  Button,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  ImageGalleryField,
  ImageUploadField,
  Input,
  PageShell,
  RadioGroup,
  RadixSelect,
  RequiredMark,
  RichTextEditor,
  SectionCard,
  toast,
} from '@/components/ui';
import { useImageUploadQueue } from '@/lib/upload';
import { ApiClientError } from '@/lib/http-core';
import { friendlyMessage } from '@/lib/errors';
import type { FundingCreateRequest, FundingRewardCreateRequest } from '@/lib/api-admin/types';
import {
  emptyFundingCreateValues,
  emptyFundingRewardValues,
  fundingCreateSchema,
  FUNDING_CATEGORIES,
  FUNDING_REWARD_TYPES,
  type FundingCreateValues,
  type FundingRewardValues,
} from '../schemas/fundingFormSchema';
import {
  useCreateFundingCampaignMutation,
  useCreateFundingRewardMutation,
  useLaunchFundingCampaignMutation,
  useUpdateFundingCampaignMutation,
} from '../hooks/useFundingMutation';
import { useFundingDetailImageUpload } from '../hooks/useFundingDetailImageUpload';
import { VendorSelector } from '@/features/vendor/components/VendorSelector';
import { ArtistSelector } from '@/features/catalog/components/ArtistSelector';
import { editorImageGuideFor, FUNDING_GALLERY_GUIDE } from '@/lib/upload/uploadGuide';
import { isoToKstLocal, kstLocalToIso } from '../lib/kstDateTime';
import { useFundingCampaignQuery } from '../hooks/useFundingCampaignQuery';
import type { Funding } from '@/lib/api-admin/types';

const errorMessageOf = (err: unknown): string | null => {
  if (!err) return null;
  if (err instanceof ApiClientError) return friendlyMessage(err, err.message ?? err.code);
  if (err instanceof Error) return err.message;
  return null;
};

/**
 * datetime-local(yyyy-MM-ddTHH:mm) → ISO. 입력은 한국 시간(KST)이다. 종전엔 뒤에 Z 를 붙여 UTC 로 보내 9시간 밀렸다
 * (오후 1시 50분 → 화면 오후 10시 50분, QA-398).
 */
export const toIso = kstLocalToIso;

const toCreateBody = (values: FundingCreateValues): FundingCreateRequest => {
  // 라이브 와이어 정본(2026-05-31): settlement create 는 goalAmount 를 **plain number** 로
  // 받는다(응답은 MoneyDto). MoneyDto 객체로 보내면 역직렬화 실패 → 500. OpenAPI 가 Money 로
  // 오선언돼 있어 cast 로 우회. initialRewards 는 BE 가 미선언(Jackson silent-drop)이라
  // 보내지 않고, create 후 표준 /rewards 엔드포인트로 순차 등록한다(toRewardCreateBody).
  // QA-404(settlement #10) — 목표 금액은 작성중 생성에서 선택. 비면 보내지 않는다(0 은 보내지 않는다).
  const body: Record<string, unknown> = { title: values.title };
  const goal = Number(values.goalAmount);
  if (values.goalAmount && Number.isFinite(goal) && goal > 0) body.goalAmount = goal;
  if (values.startAt) body.startAt = toIso(values.startAt);
  if (values.endAt) body.endAt = toIso(values.endAt);
  if (values.vendorId && values.vendorId.length > 0) body.vendorId = values.vendorId;
  if (values.artistId && values.artistId.length > 0) body.artistId = values.artistId;
  if (values.summary && values.summary.length > 0) body.summary = values.summary;
  if (values.description && values.description.length > 0)
    body.description = values.description;
  if (values.coverImageUrl && values.coverImageUrl.length > 0)
    body.coverImageUrl = values.coverImageUrl;
  // D-4/D-6b: 상세 이미지 갤러리. BE create/update 가 detailImageUrls: string[] 수용
  // (generated OpenAPI 미반영 drift라 cast 로 우회). 빈 배열은 보내지 않는다.
  if (values.detailImageUrls && values.detailImageUrls.length > 0)
    body.detailImageUrls = values.detailImageUrls;
  if (values.refundPolicy && values.refundPolicy.length > 0)
    body.refundPolicy = values.refundPolicy;
  if (values.internalNote && values.internalNote.length > 0)
    body.internalNote = values.internalNote;
  // D-1: 펀딩 카테고리(ALBUM/MD/LIGHT_STICK/ETC). 빈 값은 보내지 않는다(옵션, BE nullable).
  if (values.category && values.category.length > 0) body.category = values.category;
  return body as FundingCreateRequest;
};

/** 마지막으로 서버에 저장된 판매사·아티스트 — 바뀐 것만 PATCH 에 싣는다(그대로면 다시 검증하지 않게). */
export interface SavedLinks {
  vendorId: string;
  artistId: string;
}

/**
 * QA-404 — 임시저장한 캠페인을 다시 저장(PATCH, 작성중만).
 * settlement #11 — 판매사·아티스트·환불 정책·내부 메모도 받는다.
 *   vendorId: 바뀌었을 때만(비울 수 없다). artistId: 바뀌었을 때만, 해제는 "".
 *   refundPolicy·internalNote: 항상 현재 값, 비우면 "".
 */
export const toUpdateBody = (values: FundingCreateValues, saved?: SavedLinks): Record<string, unknown> => {
  const body: Record<string, unknown> = { title: values.title };
  const vendorId = values.vendorId ?? '';
  const artistId = values.artistId ?? '';
  if (vendorId && vendorId !== (saved?.vendorId ?? '')) body.vendorId = vendorId;
  if (artistId !== (saved?.artistId ?? '')) body.artistId = artistId;
  if (values.goalAmount && Number(values.goalAmount) > 0) body.goalAmount = Number(values.goalAmount);
  if (values.startAt) body.startAt = toIso(values.startAt);
  if (values.endAt) body.endAt = toIso(values.endAt);
  body.summary = values.summary ?? '';
  body.description = values.description ?? '';
  if (values.coverImageUrl) body.coverImageUrl = values.coverImageUrl;
  body.detailImageUrls = values.detailImageUrls ?? [];
  if (values.category) body.category = values.category;
  body.refundPolicy = values.refundPolicy ?? '';
  body.internalNote = values.internalNote ?? '';
  return body;
};

/** 금액 응답은 Money({amount}) 또는 숫자(long)로 온다. */
const amountOf = (m: unknown): string => {
  const n = typeof m === 'number' ? m : typeof m === 'object' && m !== null ? Number((m as { amount?: unknown }).amount) : NaN;
  return Number.isFinite(n) && n > 0 ? String(n) : '';
};

/** QA-404 — 임시저장한 캠페인을 등록 폼 값으로 되돌린다(이어쓰기). */
export const fundingToCreateValues = (f: Funding): FundingCreateValues => {
  const extra = f as Funding & { category?: string | null; detailImageUrls?: string[] | null };
  return {
    ...emptyFundingCreateValues(),
    vendorId: f.vendorId ?? '',
    artistId: f.artistId ?? '',
    title: f.title ?? '',
    summary: f.summary ?? '',
    description: f.description ?? '',
    category: (extra.category ?? '') as FundingCreateValues['category'],
    coverImageUrl: f.coverImageUrl ?? '',
    detailImageUrls: extra.detailImageUrls ?? [],
    goalAmount: amountOf(f.goalAmount),
    startAt: isoToKstLocal(f.startAt),
    endAt: isoToKstLocal(f.endAt),
    refundPolicy: f.refundPolicy ?? '',
    internalNote: f.internalNote ?? '',
  };
};

const MAX_DETAIL_IMAGES = 10;

// 위저드 draft 리워드 → 표준 reward 생성 요청. minPledgeAmount=plain number(라이브 정본).
const toRewardCreateBody = (r: FundingRewardValues): FundingRewardCreateRequest => {
  const reward: Record<string, unknown> = {
    tierName: r.tierName,
    type: r.type,
    minPledgeAmount: Number(r.minPledgeAmount),
    sortOrder: r.sortOrder ?? 0,
  };
  if (r.description && r.description.length > 0) reward.description = r.description;
  if (r.maxQuantity && r.maxQuantity.length > 0) reward.maxQuantity = Number(r.maxQuantity);
  if (r.estimatedDeliveryAt && r.estimatedDeliveryAt.length > 0)
    reward.estimatedDeliveryAt = r.estimatedDeliveryAt;
  return reward as unknown as FundingRewardCreateRequest;
};

interface DraftReward {
  values: FundingRewardValues;
  expanded: boolean;
  /** QA-404 — 임시저장으로 이미 서버에 등록된 리워드(다시 등록하지 않는다). */
  saved?: boolean;
}

const textareaClass =
  'w-full rounded-md border px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2';

/** Fields validated before advancing past each step (RHF trigger). Steps 3 & 6
 * have no schema-bound inputs gating progression (rewards are drafts, step 6 is
 * the confirm view). */
const STEP_FIELDS: ReadonlyArray<ReadonlyArray<keyof FundingCreateValues>> = [
  ['vendorId', 'artistId', 'title', 'summary', 'coverImageUrl'],
  ['description'],
  [],
  ['startAt', 'endAt'],
  ['goalAmount', 'refundPolicy'],
  [],
];

const TOTAL_STEPS = 6;

export const FundingCampaignCreatePage = () => {
  const { t } = useTranslation('funding');
  const navigate = useNavigate();
  // QA-404 — /funding-campaigns/:campaignId/edit 이면 임시저장한 캠페인을 이어서 쓴다(같은 등록 폼).
  const { campaignId: resumeId } = useParams<{ campaignId: string }>();
  const resumeQuery = useFundingCampaignQuery(resumeId);
  const createMutation = useCreateFundingCampaignMutation();
  const updateMutation = useUpdateFundingCampaignMutation();
  const launchMutation = useLaunchFundingCampaignMutation();
  const createRewardMutation = useCreateFundingRewardMutation();

  const form = useForm<FundingCreateValues>({
    resolver: zodResolver(fundingCreateSchema),
    defaultValues: emptyFundingCreateValues(),
    mode: 'onBlur',
  });

  const [drafts, setDrafts] = useState<DraftReward[]>([]);
  const [visibility, setVisibility] = useState<'public' | 'private'>('private');
  const [step, setStep] = useState(0);
  const [rewardError, setRewardError] = useState<string | null>(null);
  // 상세 본문(RichTextEditor) 인라인 이미지 업로드 실패 메시지(한국어). 배너로 표면화한다.
  const [imageUploadError, setImageUploadError] = useState<string | null>(null);
  const uploads = useImageUploadQueue();
  const uploadDetailImage = useFundingDetailImageUpload();
  const error =
    errorMessageOf(createMutation.error) ?? errorMessageOf(updateMutation.error) ?? rewardError ?? imageUploadError ?? uploads.error;
  // create/update + 후속 리워드 순차 등록 전체를 하나의 진행 상태로 본다.
  const submitting = createMutation.isPending || updateMutation.isPending || createRewardMutation.isPending || launchMutation.isPending;

  // QA-398·QA-404 — 저장 두 갈래.
  //  [임시저장] 작성 도중에도 저장한다 — BE 필수(제목)만 확인하고 채운 항목만 보낸다. 첫 저장은 작성중(DRAFT)
  //    캠페인을 만들고 화면에 머문다. 다음 저장부터는 같은 캠페인을 수정(PATCH)한다. 리워드는 아직 안 올린 것만 등록.
  //  [캠페인 등록] 전체 검증 후 저장(임시저장한 캠페인이 있으면 그걸 마무리) → 공개면 바로 런칭 → 상세로 이동.
  //  종전 [임시저장]은 저장 없이 목록으로만 갔고(QA-404), "공개(Launch 즉시)"를 골라도 런칭하지 않았다.
  const [savedId, setSavedId] = useState<string | null>(resumeId ?? null);
  const [savedLinks, setSavedLinks] = useState<SavedLinks>({ vendorId: '', artistId: '' });

  // 이어쓰기: 불러온 캠페인으로 폼·리워드를 채운다(한 번만). 작성중이 아니면 상세로 돌려보낸다.
  const [hydrated, setHydrated] = useState(false);
  useEffect(() => {
    const f = resumeQuery.data;
    if (!resumeId || !f || hydrated) return;
    if (f.status !== 'DRAFT') {
      navigate(`/funding-campaigns/${resumeId}`, { replace: true });
      return;
    }
    form.reset(fundingToCreateValues(f));
    setSavedLinks({ vendorId: f.vendorId ?? '', artistId: f.artistId ?? '' });
    setDrafts(
      (f.rewards ?? []).map((r, i) => ({
        expanded: false,
        saved: true,
        values: {
          ...emptyFundingRewardValues(),
          tierName: r.tierName,
          description: r.description ?? '',
          type: r.type,
          minPledgeAmount: amountOf(r.minPledgeAmount),
          maxQuantity: r.maxQuantity != null ? String(r.maxQuantity) : '',
          estimatedDeliveryAt: r.estimatedDeliveryAt ?? '',
          sortOrder: (r as { sortOrder?: number }).sortOrder ?? i,
        },
      })),
    );
    setHydrated(true);
  }, [resumeId, resumeQuery.data, hydrated, form, navigate]);
  const persist = async (values: FundingCreateValues, mode: 'draft' | 'submit') => {
    setRewardError(null);
    let resolved: Awaited<ReturnType<typeof uploads.flush>>;
    try {
      resolved = await uploads.flush();
    } catch {
      return;
    }
    const coverImageUrl = typeof resolved.coverImageUrl === 'string' ? resolved.coverImageUrl : values.coverImageUrl ?? '';
    const detailImageUrls = Array.isArray(resolved.detailImageUrls) ? resolved.detailImageUrls : (values.detailImageUrls ?? []);
    const merged: FundingCreateValues = { ...values, coverImageUrl, detailImageUrls };

    let campaignId = savedId;
    try {
      if (campaignId) {
        await updateMutation.mutateAsync({ campaignId, body: toUpdateBody(merged, savedLinks) as never });
      } else {
        const created = await createMutation.mutateAsync(toCreateBody(merged));
        campaignId = created.campaignId;
        setSavedId(campaignId);
      }
      setSavedLinks({ vendorId: merged.vendorId || savedLinks.vendorId, artistId: merged.artistId ?? '' });
    } catch {
      return; // 오류 배너(createMutation/updateMutation.error)
    }

    // 리워드 — 아직 안 올린 draft 만(BE create 가 initialRewards 를 받지 않아 표준 /rewards 로 순차 등록).
    let failed = 0;
    const savedFlags: boolean[] = [];
    for (const d of drafts) {
      if (d.saved) {
        savedFlags.push(true);
        continue;
      }
      try {
        await createRewardMutation.mutateAsync({ campaignId, body: toRewardCreateBody(d.values) });
        savedFlags.push(true);
      } catch {
        failed += 1;
        savedFlags.push(false);
      }
    }
    setDrafts((prev) => prev.map((d, i) => (savedFlags[i] ? { ...d, saved: true } : d)));
    if (failed > 0) {
      // 캠페인은 저장됨 — 화면에 머물러 알린다(다시 저장하면 실패한 리워드만 재시도).
      setRewardError(t('funding.create.rewardPartialFail', { failed }));
      return;
    }

    if (mode === 'draft') {
      toast.success(t('funding.create.draftSaved', '임시저장했습니다. 이어서 작성하거나 목록에서 다시 열 수 있습니다.'));
      return;
    }
    if (visibility === 'public') {
      try {
        await launchMutation.mutateAsync({ campaignId });
      } catch {
        // 런칭 실패(예: 리워드 없음) — 작성중으로 저장된 상세로 가서 다시 런칭한다.
        toast.error(t('funding.create.launchFailed', '캠페인은 작성중으로 저장됐지만 런칭하지 못했습니다. 상세에서 다시 런칭해 주세요.'));
      }
    }
    navigate(`/funding-campaigns/${campaignId}`);
  };

  /** [임시저장] — 제목만 확인(BE 필수). 목표 금액·나머지 단계 검증은 [캠페인 등록]·런칭에서. */
  const saveDraft = async () => {
    form.clearErrors();
    const values = form.getValues();
    let ok = true;
    if (!values.title || values.title.trim() === '') {
      form.setError('title', { message: 'funding.errors.titleRequired' });
      ok = false;
    }
    // QA-404(settlement #10) — 목표 금액은 비워도 된다(런칭 때 BE 가 확인). 넣었다면 0 보다 커야 한다.
    const goal = Number(values.goalAmount);
    if (values.goalAmount && (!Number.isFinite(goal) || goal <= 0)) {
      form.setError('goalAmount', { message: 'funding.errors.goalAmountInvalid' });
      ok = false;
    }
    if (!ok) {
      toast.error(t('funding.create.draftNeeds', '임시저장하려면 캠페인 제목을 입력해 주세요.'));
      return;
    }
    await persist({ ...values, title: values.title.trim() }, 'draft');
  };

  const addDraft = (values: FundingRewardValues) => {
    setDrafts((prev) => [...prev, { values, expanded: false }]);
  };

  const removeDraft = (index: number) => {
    setDrafts((prev) => prev.filter((_, i) => i !== index));
  };

  const toggleDraft = (index: number) => {
    setDrafts((prev) =>
      prev.map((d, i) => (i === index ? { ...d, expanded: !d.expanded } : d)),
    );
  };

  const goNext = async () => {
    const fields = STEP_FIELDS[step] ?? [];
    const ok = fields.length === 0 ? true : await form.trigger([...fields]);
    if (ok) setStep((s) => Math.min(s + 1, TOTAL_STEPS - 1));
  };

  const goPrev = () => setStep((s) => Math.max(s - 1, 0));

  const goToStep = async (target: number) => {
    if (target === step) return;
    if (target < step) {
      setStep(target);
      return;
    }
    // Moving forward via the stepper validates the current step first.
    const fields = STEP_FIELDS[step] ?? [];
    const ok = fields.length === 0 ? true : await form.trigger([...fields]);
    if (ok) setStep(target);
  };

  const stepLabels = useMemo(
    () => [
      t('funding.create.wizard.step1'),
      t('funding.create.wizard.step2'),
      t('funding.create.wizard.step3'),
      t('funding.create.wizard.step4'),
      t('funding.create.wizard.step5'),
      t('funding.create.wizard.step6'),
    ],
    [t],
  );

  const isLastStep = step === TOTAL_STEPS - 1;

  return (
    <PageShell
      title={resumeId ? t('funding.create.resumeTitle', '임시저장 캠페인 이어쓰기') : t('funding.create.title')}
      breadcrumb={[
        { label: t('funding.nav.list'), href: '/funding-campaigns' },
        { label: resumeId ? (resumeQuery.data?.title ?? '…') : t('funding.create.breadcrumbCurrent') },
      ]}
    >
      {resumeId && resumeQuery.isPending ? (
        <p className="text-sm" style={{ color: 'var(--content-muted)' }} role="status">
          {t('funding.create.resumeLoading', '임시저장한 캠페인을 불러오는 중…')}
        </p>
      ) : null}
      <WizardStepper
        labels={stepLabels}
        current={step}
        total={TOTAL_STEPS}
        counterLabel={t('funding.create.wizard.stepCounter', {
          current: step + 1,
          total: TOTAL_STEPS,
        })}
        stepperLabel={t('funding.create.stepperLabel')}
        onStepClick={goToStep}
      />

      {error ? (
        <p
          role="alert"
          aria-live="polite"
          className="mt-4 rounded-md p-3 text-sm"
          style={{
            background: 'var(--status-danger-bg)',
            color: 'var(--status-danger-fg)',
          }}
        >
          {error}
        </p>
      ) : null}

      <Form {...form}>
        <form
          onSubmit={form.handleSubmit((v) => persist(v, 'submit'))}
          aria-label={t('funding.create.formLabel')}
          noValidate
          data-testid="funding-create-form"
          className="mt-4"
        >
          {/* Step 1 — 기본 정보 */}
          <div hidden={step !== 0}>
            <SectionCard title={t('funding.create.sectionBasic')}>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <FormField
                  control={form.control}
                  name="vendorId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('funding.fields.vendorId')}</FormLabel>
                      <FormControl>
                        <VendorSelector
                          value={field.value ?? null}
                          onChange={(id) => field.onChange(id ?? '')}
                          errorMessage={form.formState.errors.vendorId?.message ?? null}
                        />
                      </FormControl>
                      <FormMessage>{form.formState.errors.vendorId?.message ? t(form.formState.errors.vendorId.message, { defaultValue: form.formState.errors.vendorId.message }) : null}</FormMessage>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="artistId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('funding.fields.artistId')}</FormLabel>
                      <FormControl>
                        <ArtistSelector
                          value={field.value ?? null}
                          vendorId={form.watch('vendorId') ?? null}
                          onChange={(id) => field.onChange(id ?? '')}
                          errorMessage={form.formState.errors.artistId?.message ?? null}
                        />
                      </FormControl>
                      <FormMessage>{form.formState.errors.artistId?.message ? t(form.formState.errors.artistId.message, { defaultValue: form.formState.errors.artistId.message }) : null}</FormMessage>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="title"
                  render={({ field }) => (
                    <FormItem className="md:col-span-2">
                      <FormLabel required>
                        {t('funding.fields.title')}
                      </FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          required
                          aria-required="true"
                          maxLength={200}
                          style={{ height: 'var(--density-compact-form-h)' }}
                        />
                      </FormControl>
                      <FormMessage>{form.formState.errors.title?.message ? t(form.formState.errors.title.message, { defaultValue: form.formState.errors.title.message }) : null}</FormMessage>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="summary"
                  render={({ field }) => (
                    <FormItem className="md:col-span-2">
                      <FormLabel>{t('funding.fields.summary')}</FormLabel>
                      <FormControl>
                        <textarea
                          {...field}
                          value={field.value ?? ''}
                          maxLength={500}
                          rows={2}
                          className={textareaClass}
                          style={{
                            background: 'var(--surface-0)',
                            color: 'var(--content-strong)',
                            borderColor: 'var(--content-muted)',
                          }}
                        />
                      </FormControl>
                      <FormMessage>{form.formState.errors.summary?.message ? t(form.formState.errors.summary.message, { defaultValue: form.formState.errors.summary.message }) : null}</FormMessage>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="category"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('funding.fields.category', { defaultValue: '카테고리' })}</FormLabel>
                      <FormControl>
                        <RadixSelect
                          id="funding-category"
                          value={field.value ?? ''}
                          onChange={(val) => field.onChange(val as FundingCreateValues['category'])}
                          placeholder={t('funding.fields.categoryNone', { defaultValue: '선택 안 함' })}
                          options={FUNDING_CATEGORIES.map((opt) => ({ value: opt, label: t(`funding.category.${opt}`, opt) }))}
                        />
                      </FormControl>
                      <FormMessage>{form.formState.errors.category?.message ? t(form.formState.errors.category.message, { defaultValue: form.formState.errors.category.message }) : null}</FormMessage>
                    </FormItem>
                  )}
                />
              </div>
            </SectionCard>

            <SectionCard title={t('funding.create.sectionCoverImage')} className="mt-5">
              <FormField
                control={form.control}
                name="coverImageUrl"
                render={({ field }) => (
                  <FormItem className="flex flex-col gap-1">
                    <FormControl>
                      <ImageUploadField
                        queue={uploads}
                        name="coverImageUrl"
                        category="FUNDING_COVER"
                        value={field.value || null}
                        onChange={(url) => field.onChange(url ?? '')}
                        label={t('funding.create.coverImageMain')}
                        disabled={createMutation.isPending}
                      />
                    </FormControl>
                    <FormMessage>{form.formState.errors.coverImageUrl?.message ? t(form.formState.errors.coverImageUrl.message, { defaultValue: form.formState.errors.coverImageUrl.message }) : null}</FormMessage>
                  </FormItem>
                )}
              />
            </SectionCard>

            <SectionCard title={t('funding.create.sectionDetailImages')} className="mt-5">
              <p className="mb-3 text-xs" style={{ color: 'var(--content-muted)' }}>
                {t('funding.create.detailImagesHint', { max: MAX_DETAIL_IMAGES })}
              </p>
              <FormField
                control={form.control}
                name="detailImageUrls"
                render={({ field }) => (
                  <FormItem className="flex flex-col gap-1">
                    <FormControl>
                      <ImageGalleryField
                        queue={uploads}
                        name="detailImageUrls"
                        category="FUNDING_COVER"
                        guide={FUNDING_GALLERY_GUIDE}
                        value={field.value ?? []}
                        onChange={(urls) => field.onChange(urls)}
                        max={MAX_DETAIL_IMAGES}
                      />
                    </FormControl>
                    <FormMessage>{form.formState.errors.detailImageUrls?.message ? t(form.formState.errors.detailImageUrls.message, { defaultValue: form.formState.errors.detailImageUrls.message }) : null}</FormMessage>
                  </FormItem>
                )}
              />
            </SectionCard>
          </div>

          {/* Step 2 — 스토리 (RichTextEditor) */}
          <div hidden={step !== 1}>
            <SectionCard title={t('funding.create.sectionStory')}>
              <p className="mb-3 text-xs" style={{ color: 'var(--content-muted)' }}>
                {t('funding.create.wizard.step2Hint')}
              </p>
              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('funding.fields.description')}</FormLabel>
                    <FormControl>
                      <RichTextEditor
                        value={field.value ?? ''}
                        onChange={field.onChange}
                        onUploadImage={uploadDetailImage}
                        imageGuide={editorImageGuideFor('FUNDING_BODY')}
                        onUploadError={(e) =>
                          setImageUploadError(
                            e instanceof Error ? e.message : '이미지 업로드에 실패했습니다.',
                          )
                        }
                        aria-label={t('funding.fields.description')}
                      />
                    </FormControl>
                    <FormMessage>{form.formState.errors.description?.message ? t(form.formState.errors.description.message, { defaultValue: form.formState.errors.description.message }) : null}</FormMessage>
                  </FormItem>
                )}
              />
            </SectionCard>
          </div>

          {/* Step 3 — 리워드 (draft array → initialRewards) */}
          <div hidden={step !== 2}>
            <SectionCard title={t('funding.create.sectionRewards')}>
              {drafts.length === 0 ? (
                <p
                  className="mb-3 rounded-md p-3 text-xs"
                  style={{
                    background: 'var(--status-warn-bg)',
                    color: 'var(--status-warn-fg)',
                  }}
                >
                  {t('funding.create.rewardsEmpty')}
                </p>
              ) : (
                <ul className="mb-3 flex flex-col gap-2" data-testid="draft-rewards">
                  {drafts.map((d, i) => (
                    <li
                      key={i}
                      className="overflow-hidden rounded-md"
                      style={{ background: 'var(--surface-1)' }}
                    >
                      <div className="flex items-center justify-between p-3">
                        <div className="flex flex-col gap-0.5">
                          <span
                            className="text-sm font-medium"
                            style={{ color: 'var(--content-strong)' }}
                          >
                            {d.values.tierName}
                          </span>
                          <span className="text-xs" style={{ color: 'var(--content-muted)' }}>
                            {Intl.NumberFormat('ko-KR').format(Number(d.values.minPledgeAmount))}{' '}
                            KRW
                          </span>
                        </div>
                        <div className="flex items-center gap-1">
                          <button
                            type="button"
                            aria-expanded={d.expanded}
                            aria-label={d.expanded ? '접기' : '펼치기'}
                            onClick={() => toggleDraft(i)}
                            className="rounded p-1 text-xs focus-visible:outline-none focus-visible:ring-2"
                            style={{ color: 'var(--content-muted)' }}
                          >
                            {d.expanded ? '▲' : '▼'}
                          </button>
                          {/* QA-404 — 임시저장으로 이미 등록된 리워드는 여기서 지우지 않는다(상세 리워드 탭에서 관리). */}
                          {d.saved ? (
                            <span className="text-[11px]" style={{ color: 'var(--content-muted)' }} data-testid="reward-draft-saved">
                              {t('funding.create.rewardSaved', '저장됨')}
                            </span>
                          ) : (
                            <button
                              type="button"
                              aria-label="리워드 삭제"
                              onClick={() => removeDraft(i)}
                              className="rounded p-1 text-xs focus-visible:outline-none focus-visible:ring-2"
                              style={{ color: 'var(--signal-danger)' }}
                            >
                              ✕
                            </button>
                          )}
                        </div>
                      </div>
                      {d.expanded ? (
                        <div
                          className="border-t px-3 pb-3 pt-2 text-xs space-y-0.5"
                          style={{ borderColor: 'var(--content-muted)', color: 'var(--content-muted)' }}
                        >
                          <div>{t('funding.reward.type')}: {t(`funding.reward.type_${d.values.type}`)}</div>
                          {d.values.maxQuantity ? (
                            <div>
                              {t('funding.reward.maxQuantity')}: {d.values.maxQuantity}
                            </div>
                          ) : null}
                        </div>
                      ) : null}
                    </li>
                  ))}
                </ul>
              )}

              <RewardAddForm onAdd={addDraft} />
            </SectionCard>
          </div>

          {/* Step 4 — 일정 */}
          <div hidden={step !== 3}>
            <SectionCard title={t('funding.create.sectionSchedule')}>
              <p className="mb-3 text-xs" style={{ color: 'var(--content-muted)' }}>
                {t('funding.create.wizard.step4Hint')}
              </p>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <FormField
                  control={form.control}
                  name="startAt"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel required>
                        {t('funding.fields.startAt')}
                      </FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          type="datetime-local"
                          required
                          aria-required="true"
                          style={{ height: 'var(--density-compact-form-h)' }}
                        />
                      </FormControl>
                      <FormMessage>{form.formState.errors.startAt?.message ? t(form.formState.errors.startAt.message, { defaultValue: form.formState.errors.startAt.message }) : null}</FormMessage>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="endAt"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel required>
                        {t('funding.fields.endAt')}
                      </FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          type="datetime-local"
                          required
                          aria-required="true"
                          style={{ height: 'var(--density-compact-form-h)' }}
                        />
                      </FormControl>
                      <FormMessage>{form.formState.errors.endAt?.message ? t(form.formState.errors.endAt.message, { defaultValue: form.formState.errors.endAt.message }) : null}</FormMessage>
                    </FormItem>
                  )}
                />
              </div>
            </SectionCard>
          </div>

          {/* Step 5 — 정산 */}
          <div hidden={step !== 4}>
            <SectionCard title={t('funding.create.sectionSettlement')}>
              <p className="mb-3 text-xs" style={{ color: 'var(--content-muted)' }}>
                {t('funding.create.wizard.step5Hint')}
              </p>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <FormField
                  control={form.control}
                  name="goalAmount"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel required>
                        {t('funding.fields.goalAmount')}
                      </FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          required
                          aria-required="true"
                          inputMode="decimal"
                          style={{ height: 'var(--density-compact-form-h)' }}
                        />
                      </FormControl>
                      <FormMessage>{form.formState.errors.goalAmount?.message ? t(form.formState.errors.goalAmount.message, { defaultValue: form.formState.errors.goalAmount.message }) : null}</FormMessage>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="refundPolicy"
                  render={({ field }) => (
                    <FormItem className="md:col-span-2">
                      <FormLabel>{t('funding.fields.refundPolicy')}</FormLabel>
                      <FormControl>
                        <textarea
                          {...field}
                          value={field.value ?? ''}
                          maxLength={2000}
                          rows={3}
                          className={textareaClass}
                          style={{
                            background: 'var(--surface-0)',
                            color: 'var(--content-strong)',
                            borderColor: 'var(--content-muted)',
                          }}
                        />
                      </FormControl>
                      <FormMessage>{form.formState.errors.refundPolicy?.message ? t(form.formState.errors.refundPolicy.message, { defaultValue: form.formState.errors.refundPolicy.message }) : null}</FormMessage>
                    </FormItem>
                  )}
                />
              </div>
            </SectionCard>
          </div>

          {/* Step 6 — 검수 및 공개 */}
          <div hidden={step !== 5}>
            <SectionCard title={t('funding.create.confirmHeading')}>
              <p className="mb-4 text-xs" style={{ color: 'var(--content-muted)' }}>
                {t('funding.create.confirmHint')}
              </p>

              <ReviewSummary form={form} draftCount={drafts.length} />

              <div className="mt-5 flex flex-col gap-2">
                <span className="text-sm font-medium" style={{ color: 'var(--content-strong)' }}>
                  {t('funding.create.visibility')}
                </span>
                <div className="flex items-center gap-4 pt-1">
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input
                      type="radio"
                      name="visibility"
                      value="private"
                      checked={visibility === 'private'}
                      onChange={() => setVisibility('private')}
                      className="accent-[color:var(--accent-default)]"
                    />
                    <span style={{ color: 'var(--content-strong)' }}>
                      {t('funding.create.visibilityPrivate')}
                    </span>
                  </label>
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input
                      type="radio"
                      name="visibility"
                      value="public"
                      checked={visibility === 'public'}
                      onChange={() => setVisibility('public')}
                      className="accent-[color:var(--accent-default)]"
                    />
                    <span style={{ color: 'var(--content-strong)' }}>
                      {t('funding.create.visibilityPublic')}
                    </span>
                  </label>
                </div>
              </div>

              <FormField
                control={form.control}
                name="internalNote"
                render={({ field }) => (
                  <FormItem className="mt-5">
                    <FormLabel>{t('funding.fields.internalNote')}</FormLabel>
                    <FormControl>
                      <textarea
                        {...field}
                        value={field.value ?? ''}
                        maxLength={2000}
                        rows={2}
                        className={textareaClass}
                        style={{
                          background: 'var(--surface-0)',
                          color: 'var(--content-strong)',
                          borderColor: 'var(--content-muted)',
                        }}
                      />
                    </FormControl>
                    <FormMessage>{form.formState.errors.internalNote?.message ? t(form.formState.errors.internalNote.message, { defaultValue: form.formState.errors.internalNote.message }) : null}</FormMessage>
                  </FormItem>
                )}
              />
            </SectionCard>
          </div>

          {/* Wizard nav */}
          <div className="mt-5 flex items-center justify-between">
            <Button
              type="button"
              variant="outline"
              disabled={step === 0 || submitting}
              onClick={goPrev}
            >
              {t('funding.actions.prev')}
            </Button>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                disabled={submitting || uploads.isUploading}
                onClick={() => void saveDraft()}
                data-testid="funding-save-draft"
              >
                {t('funding.actions.saveDraft')}
              </Button>
              {isLastStep ? (
                <Button
                  type="button"
                  variant="cta"
                  disabled={submitting || uploads.isUploading}
                  onClick={() => void form.handleSubmit((v) => persist(v, 'submit'))()}
                  data-testid="funding-create-submit"
                >
                  {uploads.isUploading
                    ? t('funding.create.uploadingImage', { defaultValue: '이미지 업로드 중…' })
                    : submitting
                      ? t('funding.create.submitting')
                      : t('funding.actions.create')}
                </Button>
              ) : (
                <Button type="button" variant="cta" onClick={goNext}>
                  {t('funding.actions.next')}
                </Button>
              )}
            </div>
          </div>
        </form>
      </Form>
    </PageShell>
  );
};

interface WizardStepperProps {
  labels: ReadonlyArray<string>;
  current: number;
  total: number;
  counterLabel: string;
  stepperLabel: string;
  onStepClick: (index: number) => void;
}

const WizardStepper = ({
  labels,
  current,
  total,
  counterLabel,
  stepperLabel,
  onStepClick,
}: WizardStepperProps) => (
  <nav aria-label={stepperLabel} data-testid="funding-wizard-stepper">
    <ol className="flex flex-wrap items-center gap-2">
      {labels.map((label, index) => {
        const isActive = index === current;
        const isDone = index < current;
        return (
          <li key={label} className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => onStepClick(index)}
              aria-current={isActive ? 'step' : undefined}
              className="flex items-center gap-2 rounded-md px-3 py-1.5 text-xs font-medium transition-colors"
              style={{
                background: isActive ? 'var(--accent-default)' : 'var(--surface-1)',
                color: isActive
                  ? 'var(--cta-primary-on)'
                  : isDone
                    ? 'var(--content-strong)'
                    : 'var(--content-muted)',
                border: '1px solid var(--divider-strong)',
              }}
            >
              <span
                aria-hidden="true"
                className="flex h-5 w-5 items-center justify-center rounded-full text-[11px]"
                style={{
                  background: isActive
                    ? 'var(--cta-primary-on)'
                    : isDone
                      ? 'var(--accent-default)'
                      : 'var(--surface-raised)',
                  color: isActive
                    ? 'var(--accent-default)'
                    : isDone
                      ? 'var(--cta-primary-on)'
                      : 'var(--content-muted)',
                }}
              >
                {index + 1}
              </span>
              {label}
            </button>
            {index < total - 1 ? (
              <span aria-hidden="true" style={{ color: 'var(--content-muted)' }}>›</span>
            ) : null}
          </li>
        );
      })}
    </ol>
    <p className="mt-2 text-xs" style={{ color: 'var(--content-muted)' }}>
      {counterLabel}
    </p>
  </nav>
);

interface ReviewSummaryProps {
  form: ReturnType<typeof useForm<FundingCreateValues>>;
  draftCount: number;
}

const ReviewSummary = ({ form, draftCount }: ReviewSummaryProps) => {
  const { t } = useTranslation('funding');
  const v = form.watch();
  const fallback = t('funding.create.wizard.summaryEmpty');
  const rows: ReadonlyArray<{ label: string; value: string }> = [
    { label: t('funding.fields.title'), value: v.title || fallback },
    { label: t('funding.fields.summary'), value: v.summary || fallback },
    { label: t('funding.fields.goalAmount'), value: v.goalAmount ? `${Intl.NumberFormat('ko-KR').format(Number(v.goalAmount))} KRW` : fallback },
    { label: t('funding.fields.startAt'), value: v.startAt || fallback },
    { label: t('funding.fields.endAt'), value: v.endAt || fallback },
    { label: t('funding.create.sectionRewards'), value: t('funding.create.rewardsCountLabel', { count: draftCount }) },
  ];

  return (
    <dl
      className="grid grid-cols-1 gap-x-6 gap-y-2 rounded-md p-4 md:grid-cols-2"
      style={{ background: 'var(--surface-1)' }}
      aria-label={t('funding.create.wizard.summaryTitle')}
    >
      {rows.map((row) => (
        <div key={row.label} className="flex flex-col gap-0.5">
          <dt className="text-xs" style={{ color: 'var(--content-muted)' }}>
            {row.label}
          </dt>
          <dd className="text-sm" style={{ color: 'var(--content-strong)' }}>
            {row.value}
          </dd>
        </div>
      ))}
    </dl>
  );
};

interface RewardAddFormProps {
  onAdd: (v: FundingRewardValues) => void;
}

const RewardAddForm = ({ onAdd }: RewardAddFormProps) => {
  const { t } = useTranslation('funding');
  const [open, setOpen] = useState(false);
  const [v, setV] = useState<FundingRewardValues>(emptyFundingRewardValues());
  const [errors, setErrors] = useState<Partial<Record<keyof FundingRewardValues, string>>>({});

  const add = () => {
    const issues: typeof errors = {};
    if (!v.tierName || v.tierName.length === 0)
      issues.tierName = 'funding.errors.tierNameRequired';
    if (!v.minPledgeAmount || Number(v.minPledgeAmount) <= 0)
      issues.minPledgeAmount = 'funding.errors.minPledgeAmountInvalid';
    setErrors(issues);
    if (Object.keys(issues).length > 0) return;
    onAdd(v);
    setV(emptyFundingRewardValues());
    setOpen(false);
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="w-full rounded-md border-2 border-dashed py-3 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2"
        style={{
          borderColor: 'var(--content-muted)',
          color: 'var(--content-muted)',
        }}
        onMouseEnter={(e) => {
          (e.currentTarget as HTMLButtonElement).style.borderColor = 'var(--accent-default)';
          (e.currentTarget as HTMLButtonElement).style.color = 'var(--accent-default)';
        }}
        onMouseLeave={(e) => {
          (e.currentTarget as HTMLButtonElement).style.borderColor = 'var(--content-muted)';
          (e.currentTarget as HTMLButtonElement).style.color = 'var(--content-muted)';
        }}
      >
        + {t('funding.reward.addAction')}
      </button>
    );
  }

  return (
    <div
      className="flex flex-col gap-3 rounded-lg p-3"
      style={{ background: 'var(--surface-1)' }}
    >
      <div className="flex flex-col gap-1">
        <label
          htmlFor="draft-tierName"
          className="text-sm font-medium"
          style={{ color: errors.tierName ? 'var(--status-danger-fg)' : 'var(--content-strong)' }}
        >
          {t('funding.reward.tierName')}
          <RequiredMark />
        </label>
        <Input
          id="draft-tierName"
          value={v.tierName}
          onChange={(e) => setV({ ...v, tierName: e.target.value })}
          maxLength={100}
          required
          aria-required="true"
          aria-invalid={Boolean(errors.tierName)}
          style={{ height: 'var(--density-compact-form-h)' }}
        />
        {errors.tierName ? (
          <p role="alert" className="text-sm font-medium" style={{ color: 'var(--status-danger-fg)' }}>
            {t(errors.tierName, { defaultValue: errors.tierName })}
          </p>
        ) : null}
      </div>

      {/* [237] 리워드 설명(선택, 최대 2000자). RewardManager 와 동일 필드를 위저드
          인라인 추가 폼에도 노출해 등록 시 설명을 입력할 수 있게 한다. 평문 textarea
          (HTML 미주입). 스키마·기본값·생성 본문(toRewardCreateBody)은 이미 description 처리. */}
      <div className="flex flex-col gap-1">
        <label
          htmlFor="draft-description"
          className="text-sm font-medium"
          style={{ color: 'var(--content-strong)' }}
        >
          {t('funding.reward.description')}
        </label>
        <textarea
          id="draft-description"
          value={v.description ?? ''}
          onChange={(e) => setV({ ...v, description: e.target.value })}
          maxLength={2000}
          rows={3}
          className={textareaClass}
          style={{
            background: 'var(--surface-0)',
            color: 'var(--content-strong)',
            borderColor: 'var(--content-muted)',
          }}
        />
      </div>

      <div className="flex flex-col gap-1.5">
        <span id="draft-type-label" className="text-sm font-medium" style={{ color: 'var(--content-strong)' }}>
          {t('funding.reward.type')}
        </span>
        <RadioGroup
          value={v.type}
          onValueChange={(val) => setV({ ...v, type: val as FundingRewardValues['type'] })}
          options={FUNDING_REWARD_TYPES.map((opt) => ({
            value: opt,
            label: t(`funding.reward.type_${opt}`),
          }))}
          orientation="horizontal"
          aria-labelledby="draft-type-label"
        />
      </div>

      <div className="flex flex-col gap-1">
        <label
          htmlFor="draft-minPledgeAmount"
          className="text-sm font-medium"
          style={{ color: errors.minPledgeAmount ? 'var(--status-danger-fg)' : 'var(--content-strong)' }}
        >
          {t('funding.reward.minPledgeAmount')}
          <RequiredMark />
        </label>
        <Input
          id="draft-minPledgeAmount"
          value={v.minPledgeAmount}
          onChange={(e) => setV({ ...v, minPledgeAmount: e.target.value })}
          inputMode="decimal"
          required
          aria-required="true"
          aria-invalid={Boolean(errors.minPledgeAmount)}
          style={{ height: 'var(--density-compact-form-h)' }}
        />
        {errors.minPledgeAmount ? (
          <p role="alert" className="text-sm font-medium" style={{ color: 'var(--status-danger-fg)' }}>
            {t(errors.minPledgeAmount, { defaultValue: errors.minPledgeAmount })}
          </p>
        ) : null}
      </div>

      <div className="flex flex-col gap-1">
        <label htmlFor="draft-maxQuantity" className="text-sm font-medium" style={{ color: 'var(--content-strong)' }}>
          {t('funding.reward.maxQuantity')}
        </label>
        <Input
          id="draft-maxQuantity"
          value={v.maxQuantity ?? ''}
          onChange={(e) => setV({ ...v, maxQuantity: e.target.value })}
          inputMode="numeric"
          style={{ height: 'var(--density-compact-form-h)' }}
        />
      </div>

      <div className="flex flex-col gap-1">
        <label htmlFor="draft-estimatedDeliveryAt" className="text-sm font-medium" style={{ color: 'var(--content-strong)' }}>
          {t('funding.reward.estimatedDeliveryAt')}
        </label>
        <Input
          id="draft-estimatedDeliveryAt"
          type="date"
          value={v.estimatedDeliveryAt ?? ''}
          onChange={(e) => setV({ ...v, estimatedDeliveryAt: e.target.value })}
          style={{ height: 'var(--density-compact-form-h)' }}
        />
      </div>

      <div className="flex gap-2">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => {
            setOpen(false);
            setErrors({});
            setV(emptyFundingRewardValues());
          }}
        >
          {t('funding.actions.cancel')}
        </Button>
        <Button type="button" variant="cta" size="sm" onClick={add}>
          {t('funding.reward.addAction')}
        </Button>
      </div>
    </div>
  );
};
